import type { ComponentDef } from './componentLibrary.ts';

interface PlacedComponent extends ComponentDef {
  instanceId: string;
}

export class DropCanvas {
  private items: PlacedComponent[] = [];
  private zone: HTMLElement;
  private emptyMsg: HTMLElement;
  private list: HTMLElement;
  private counter: HTMLElement;
  private dragFromPaletteInProgress = false;
  private reorderDragIndex = -1;

  constructor() {
    this.zone = document.getElementById('drop-zone')!;
    this.emptyMsg = document.getElementById('drop-zone-empty')!;
    this.list = document.getElementById('drop-zone-items')!;
    this.counter = document.getElementById('components-counter')!;
    this.bindZoneEvents();
  }

  add(comp: ComponentDef): void {
    this.items.push({ ...comp, instanceId: `${comp.id}-${Date.now()}` });
    this.render();
  }

  clear(): void {
    this.items = [];
    this.render();
  }

  hasItems(): boolean {
    return this.items.length > 0;
  }

  getLayoutPrompt(): string {
    const list = this.items.map((c, i) => `${i + 1}. ${c.label}`).join('\n');
    return `Build a complete, polished, responsive website using the following components in this exact order:\n\n${list}\n\nMake it beautiful, modern, and production-quality with realistic placeholder content.`;
  }

  private remove(instanceId: string): void {
    this.items = this.items.filter((i) => i.instanceId !== instanceId);
    this.render();
  }

  private reorder(from: number, to: number): void {
    const [moved] = this.items.splice(from, 1);
    this.items.splice(to, 0, moved);
    this.render();
  }

  private bindZoneEvents(): void {
    this.zone.addEventListener('dragover', (e) => {
      e.preventDefault();
      // Only highlight when dragging from palette (not internal reorder)
      if (this.reorderDragIndex === -1) {
        this.zone.classList.add('drag-over');
      }
    });

    this.zone.addEventListener('dragleave', (e) => {
      if (!this.zone.contains(e.relatedTarget as Node)) {
        this.zone.classList.remove('drag-over');
      }
    });

    this.zone.addEventListener('drop', (e) => {
      e.preventDefault();
      this.zone.classList.remove('drag-over');
      const data = e.dataTransfer?.getData('application/component');
      if (data) {
        this.add(JSON.parse(data) as ComponentDef);
      }
    });
  }

  private render(): void {
    const isEmpty = this.items.length === 0;
    this.emptyMsg.style.display = isEmpty ? 'flex' : 'none';
    this.list.innerHTML = '';

    this.items.forEach((item, index) => {
      const el = document.createElement('div');
      el.className = 'dropped-item';
      el.draggable = true;
      el.dataset.index = String(index);
      el.innerHTML = `
        <div class="dropped-item-drag" title="Drag to reorder">⠿</div>
        <span class="dropped-item-emoji">${item.emoji}</span>
        <span class="dropped-item-label">${item.label}</span>
        <span class="dropped-item-desc">${item.description}</span>
        <button class="dropped-item-remove" title="Remove">×</button>
      `;

      // Internal reorder drag
      el.addEventListener('dragstart', (e) => {
        this.reorderDragIndex = index;
        e.dataTransfer!.effectAllowed = 'move';
        // Prevent the zone dragover from triggering palette-drop highlight
        setTimeout(() => el.classList.add('dragging'), 0);
      });

      el.addEventListener('dragend', () => {
        this.reorderDragIndex = -1;
        el.classList.remove('dragging');
        document.querySelectorAll('.dropped-item').forEach((d) =>
          d.classList.remove('drag-target-above', 'drag-target-below')
        );
      });

      el.addEventListener('dragover', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.reorderDragIndex === -1 || this.reorderDragIndex === index) return;
        el.classList.remove('drag-target-above', 'drag-target-below');
        const rect = el.getBoundingClientRect();
        const mid = rect.top + rect.height / 2;
        el.classList.add(e.clientY < mid ? 'drag-target-above' : 'drag-target-below');
      });

      el.addEventListener('dragleave', () => {
        el.classList.remove('drag-target-above', 'drag-target-below');
      });

      el.addEventListener('drop', (e) => {
        e.preventDefault();
        e.stopPropagation();
        el.classList.remove('drag-target-above', 'drag-target-below');
        if (this.reorderDragIndex !== -1 && this.reorderDragIndex !== index) {
          this.reorder(this.reorderDragIndex, index);
        }
      });

      el.querySelector('.dropped-item-remove')!.addEventListener('click', () =>
        this.remove(item.instanceId)
      );

      this.list.appendChild(el);
    });

    const count = this.items.length;
    this.counter.textContent = `${count} component${count !== 1 ? 's' : ''}`;
  }
}
